/*
 *   File : upsert-story.js
 *   Author : https://evoqins.com
 *   Description : upsert story component (API integrated)
 *   Integrations : story/view-detail, story/upsert
 *   Version : 1.0.1
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CheckboxSelect,
  CustomFileUpload,
  Input,
  RadioGroup,
  TextEditor,
} from "../../Components/FormElements";
import { PrimaryButton } from "../../Components/Buttons";
import { useDispatch } from "react-redux";
import {
  setNavLink,
  setPageTitle,
  setSearchEnabled,
} from "../../Redux/Ui/ui.slice";
import { AddStorySlide } from "./Helper";
import { APIService } from "../../Service";
import { Loader, EmptyScreen } from "../../Components/Other";
import { toast } from "react-toastify";
import { ConfirmationModal, SuccessModal } from "../../Components/Modals";

export default function UpsertStory() {
  const { id } = useParams();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  useEffect(() => {
    if (id) {
      dispatch(setPageTitle(["Manage Story", "Update Story"]));
    } else {
      dispatch(setPageTitle(["Manage Story", "Add Story"]));
    }
    dispatch(setNavLink(["/manage-story"]));
    dispatch(setSearchEnabled(false));
  }, []);

  /* ----------------------------------
     FORM STATES
  ------------------------------------ */
  const [coverImage, setCoverImage] = useState(null); // can be URL string or file-like object
  const [storyDuration, setStoryDuration] = useState();
  const [redirectionLink, setRedirectionLink] = useState({}); // CheckboxSelect value (array)
  const [slides, setSlides] = useState([]); // child returns [{ file }]
  const [slidErrors, setSlidErrors] = useState([]); // errors per slide

  // Additional slide/file helper states are optional — usually the upload component
  // manages upload internally and returns the URL via onUploadComplete. We'll still
  // keep per-slide error tracking above (slidErrors).
  const [html, setHtml] = useState("");
  const [showError, setShowError] = useState(false);

  const [secondsOptions, setSecondsOptions] = useState([]);
  const [response, setResponse] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  /* ----------------------------------
     ERROR STATES
  ------------------------------------ */
  const [errors, setErrors] = useState({
    coverImage: "",
    storyDuration: "",
    redirectionLink: "",
  });

  const [loader, setLoader] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    _getFilters();
  }, []);

  useEffect(() => {
    errors;
  }, [slides]);

  // service to get filters data
  const _getFilters = async () => {
    try {
      // Seconds filter
      const secondsRes = await APIService(false, "story/filters/seconds");
      if (secondsRes.status_code === 200 && Array.isArray(secondsRes.data)) {
        setSecondsOptions(secondsRes.data);
      }

      // Redirection filter
      // const redirectRes = await APIService(false, "story/filters/redirection");
      // if (redirectRes.status_code === 200 && redirectRes.data) {
      //   setRedirectionOptions(redirectRes.data);
      // }
    } catch (err) {
      console.error("Error fetching story filters:", err);
    }
  };

  /* ----------------------------------
     Fetch story details when editing
  ------------------------------------ */
  useEffect(() => {
    if (!id) return;

    const _getStory = async () => {
      setLoader(true);
      try {
        const payload = { story_id: Number(id) };
        const res = await APIService(true, "story/view-detail", payload);

        if (res.status_code === 200 && res.data) {
          const d = res.data;
          console.log(d);

          setCoverImage(d.cover_image || null);

          // slide_duration -> storyDuration (store as string to work with RadioGroup)
          setStoryDuration(d.slide_duration);

          setHtml(d.content || "");

          //setting response
          setResponse(d);
          // slides -> AddStorySlide expects slides with { file: <url|string> } shape
          const mappedSlides =
            Array.isArray(d.slides) && d.slides.length
              ? d.slides.map((s) => ({ file: s.image_url || s.image || "" }))
              : [];
          setSlides(mappedSlides);
          setSlidErrors(mappedSlides.map(() => ""));
          setNotFound(false);
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error("Error fetching story detail:", error);
        setNotFound(true);
      } finally {
        setLoader(false);
      }
    };

    _getStory();
  }, [id]);

  //service to delete story
  const _deleteStory = async () => {
    setLoader(true);
    try {
      const payload = { story_id: Number(id) };
      const res = await APIService(true, "story/delete", payload);
      if (res.status_code === 200) {
        toast.success(res.message);
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoader(false);
    }
  };

  /* ----------------------------------
     VALIDATION HANDLER (Form + Slides)
  ------------------------------------ */
  const validateForm = () => {
    let valid = true;

    // Main form errors
    const newErrors = {
      coverImage: "",
      storyDuration: "",
      redirectionLink: "",
    };

    if (!coverImage) {
      newErrors.coverImage = "Cover image is required.";
      valid = false;
    }

    if (!storyDuration) {
      newErrors.storyDuration = "Please select a story duration.";
      valid = false;
    }

    if (!html) {
      setShowError("Please add content");
      valid = false;
    }
    // Slide validation
    const slideErr = slides.map((slide) =>
      !slide.file ? "Please upload an image" : "",
    );
    console.log(slideErr);

    setSlidErrors(slideErr);

    if (slideErr.some((e) => e !== "")) valid = false;

    setErrors(newErrors);
    return valid;
  };

  /* ----------------------------------
     Helper: normalize file/url to image_url string
  ------------------------------------ */
  const extractUrlFromFile = (fileLike) => {
    if (!fileLike) return "";
    if (typeof fileLike === "string") return fileLike;
    // common shapes: { url: '...', image_url: '...', fileUrl: '...', name: ... }
    if (fileLike.image_url) return fileLike.image_url;
    if (fileLike.url) return fileLike.url;
    if (fileLike.fileUrl) return fileLike.fileUrl;
    // if the upload component returns an object with 'file' holding url
    if (fileLike.file && typeof fileLike.file === "string")
      return fileLike.file;
    return "";
  };

  /* ----------------------------------
     FILE / SLIDE HANDLERS (ADDED)
     These are the "missing states/handlers" you referred to.
     - handleCoverUploadComplete updates coverImage when CustomFileUpload returns a url
     - handleFileSelect / handleFileClear / handleFileUploadComplete manage slides array
  ------------------------------------ */
  const handleCoverUploadComplete = (url) => {
    setCoverImage(url);
    setErrors((e) => ({ ...e, coverImage: "" }));
  };

  const handleCoverChange = (file) => {
    setCoverImage(file);
    setErrors((e) => ({ ...e, coverImage: "" }));
  };

  const handleCoverClear = () => {
    setCoverImage(null);
    setErrors((e) => ({ ...e, coverImage: "" }));
  };

  const handleFileSelect = (idx, file) => {
    const copy = Array.isArray(slides) ? [...slides] : [];
    // ensure slot exists
    copy[idx] = { ...(copy[idx] || {}), file };
    setSlides(copy);
    setSlidErrors((prev) => {
      const p = [...(prev || [])];
      p[idx] = "";
      return p;
    });
  };

  const handleFileClear = (idx) => {
    const copy = Array.isArray(slides) ? [...slides] : [];
    copy[idx] = { ...(copy[idx] || {}), file: null };
    setSlides(copy);
    setSlidErrors((prev) => {
      const p = [...(prev || [])];
      p[idx] = "";
      return p;
    });
  };

  const handleFileUploadComplete = (idx, url) => {
    const copy = Array.isArray(slides) ? [...slides] : [];
    copy[idx] = { ...(copy[idx] || {}), file: url };
    setSlides(copy);
    setSlidErrors((prev) => {
      const p = [...(prev || [])];
      p[idx] = "";
      return p;
    });
  };

  /* ----------------------------------
     SUBMIT HANDLER
  ------------------------------------ */
  const handleSubmit = async () => {
    if (!validateForm()) return;

    // build slides payload expected by API: [{ image_url: "..." }, ...]
    const slidesPayload = slides
      .map((s) => {
        const url = extractUrlFromFile(s.file);
        return url ? { image_url: url } : null;
      })
      .filter(Boolean);

    // cover_image must be a URL
    const cover_image = extractUrlFromFile(coverImage);

    // payload shape
    const payload = {
      story_id: id ? id : null,
      cover_image,
      slide_duration: Number(storyDuration),
      content: html || "",
      slides: slidesPayload,
    };
    setLoader(true);
    try {
      const res = await APIService(true, "story/upsert", payload);
      if (res.status_code === 200) {
        toast.success(res.message || "Story saved successfully");
        navigate("/manage-story");
      } else {
        toast.error(res.message || "Failed to save story");
        console.error("story/upsert error:", res);
      }
    } catch (error) {
      console.error("Error calling story/upsert:", error);
      toast.error(error.message || "Something went wrong");
    } finally {
      setLoader(false);
    }
  };

  if (loader) return <Loader />;
  if (notFound)
    return <EmptyScreen message={"Story not found or an error occurred"} />;

  return (
    <div className="row">
      <div className="col-12">
        <div className="e-glass-card rounded-24 d-flex flex-column p-24 bg-transparent">
          <div className="row">
            <div className="col-12">
              <div className="e-glass-card rounded-24 d-flex flex-column p-24">
                <div className="row">
                  <div className="d-flex justify-content-between align-items-center">
                    <h3 className="fw-500 fs-18 lh-20 text-eerie-black">
                      {id ? "Update" : "Add"} Story
                    </h3>
                    {id && (
                      <PrimaryButton
                        ClassName="bg-transparent fs-14 fw-500 text-copper-red border-copper-red px-3 py-2 rounded-pill"
                        name="Delete Story"
                        onPress={() => setShowModal(true)}
                      />
                    )}
                  </div>
                  <div className="col-lg-6 col-12">
                    {/* Cover Image Upload */}
                    <div className="d-flex flex-column row-gap-2 pb-2">
                      <p className="mb-0 fw-400 fs-14">
                        Upload Cover Image{" "}
                        <span className="text-cg-red">*</span>
                      </p>

                      <CustomFileUpload
                        type={2}
                        fileSize={3}
                        error={errors.coverImage}
                        file={coverImage}
                        handleChange={handleCoverChange}
                        handleClear={handleCoverClear}
                        onUploadComplete={handleCoverUploadComplete}
                      />
                    </div>
                    {/* STORY SLIDES SECTION */}
                    <AddStorySlide
                      slides={slides}
                      errors={slidErrors}
                      onChange={(updatedSlides) => setSlides(updatedSlides)}
                      handleFileSelect={handleFileSelect}
                      handleFileClear={handleFileClear}
                      handleFileUploadComplete={handleFileUploadComplete}
                    />
                    {/* Settings Section */}
                    <div className="d-flex row-gap-3 flex-column">
                      <h3 className="fw-500 fs-18 lh-20 text-eerie-black mb-0">
                        Settings
                      </h3>

                      <div className="e-glass-card shadow-none rounded-16 d-flex flex-column p-3">
                        {/* Story Duration */}
                        <RadioGroup
                          label="Select Duration"
                          require={true}
                          options={secondsOptions}
                          value={storyDuration}
                          onChange={(v) => {
                            setStoryDuration(v);
                            setErrors((e) => ({ ...e, storyDuration: "" }));
                          }}
                        />

                        {errors.storyDuration && (
                          <div className="text-cg-red fs-12 mt-1 ps-1">
                            {errors.storyDuration}
                          </div>
                        )}

                        <p className="mb-0 fw-400 fs-14 mt-2">
                          Content<span className="text-cg-red">*</span>
                        </p>
                        <TextEditor
                          value={html}
                          onChange={(v) => {
                            setHtml(v);
                            setShowError("");
                          }}
                          error={showError}
                          placeholder="Add Description..."
                          className={showError ? "has-error" : ""}
                        />
                        {showError && (
                          <p className="text-danger pt-8 fs-12">
                            {showError}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Footer Buttons */}
                    <div className="row row-cols-2 pt-24">
                      <div className="col">
                        <PrimaryButton
                          name="Cancel"
                          ClassName="rounded-pill bg-transparent border-oxford-blue py-3 text-oxford-blue w-100 fs-16 fw-500"
                          onPress={() => navigate("/manage-story")}
                        />
                      </div>

                      <div className="col">
                        <PrimaryButton
                          name={id ? "Update" : "Add"}
                          ClassName="rounded-pill bg-oxford-blue py-3 text-white w-100 fs-16 fw-500"
                          onPress={handleSubmit}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        open={showModal}
        title="Delete Story"
        message="Are you sure you want to delete this story?"
        onConfirm={_deleteStory}
        onCancel={() => setShowModal(false)}
      />
      <SuccessModal
        open={showSuccessModal}
        title={"Story Deleted"}
        message={"Story Deleted Successfully"}
        onConfirm={() => {
          setShowModal(false);
          navigate("/manage-story");
          setShowSuccessModal(false);
        }}
      />
    </div>
  );
}
